import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import './TaskModal.css';

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  owner: string | null;
  due_date: string | null;
  task_type: string | null;
  person_id: string | null;
  person_name?: string | null;
  company_id: string | null;
  company_name?: string | null;
  project_id: string | null;
  project_name?: string | null;
}

interface CompanySuggestion {
  id: string;
  company_name: string;
}

interface PersonSuggestion {
  id: string;
  full_name: string;
  company_name: string | null;
}

interface ProjectSuggestion {
  id: string;
  project_name: string;
  company_name: string | null;
}

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  taskId?: string | null;
}

const MIN_QUERY_LEN = 2;
const MAX_SUGGESTIONS = 5;

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, onSave, taskId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('To do');
  const [owner, setOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [taskType, setTaskType] = useState('');

  // CRM link selection state (typeahead)
  const [personSearch, setPersonSearch] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [selectedPersonName, setSelectedPersonName] = useState('');
  const [personSuggestions, setPersonSuggestions] = useState<PersonSuggestion[]>([]);
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(false);

  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState<CompanySuggestion[]>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [projectSuggestions, setProjectSuggestions] = useState<ProjectSuggestion[]>([]);
  const [showProjectSuggestions, setShowProjectSuggestions] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [loadedLinks, setLoadedLinks] = useState<{
    person_id: string | null;
    person_name: string | null;
    company_id: string | null;
    company_name: string | null;
    project_id: string | null;
    project_name: string | null;
  } | null>(null);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStatus('To do');
    setOwner('');
    setDueDate('');
    setTaskType('');

    setPersonSearch('');
    setSelectedPersonId(null);
    setSelectedPersonName('');
    setPersonSuggestions([]);
    setShowPersonSuggestions(false);

    setCompanySearch('');
    setSelectedCompanyId(null);
    setSelectedCompanyName('');
    setCompanySuggestions([]);
    setShowCompanySuggestions(false);

    setProjectSearch('');
    setSelectedProjectId(null);
    setSelectedProjectName('');
    setProjectSuggestions([]);
    setShowProjectSuggestions(false);

    setLoadedLinks(null);
    setError(null);
  };

  const loadTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/github-ops-tasks');
      if (!res.ok) throw new Error('fetch failed');
      const json = (await res.json()) as { success: boolean; data: Task[] };
      if (!json.success) throw new Error('api failed');

      const task = json.data.find((t) => t.id === taskId);
      if (!task) throw new Error('Task not found');

      setTitle(task.title);
      setDescription(task.description || '');
      setStatus(task.status);
      setOwner(task.owner || '');
      setDueDate(task.due_date || '');
      setTaskType(task.task_type || '');

      // Prefill CRM links using returned names when available, otherwise fall back to IDs.
      const personLabel = (task.person_name ?? '').trim() || (task.person_id || '');
      const companyLabel = (task.company_name ?? '').trim() || (task.company_id || '');
      const projectLabel = (task.project_name ?? '').trim() || (task.project_id || '');

      setLoadedLinks({
        person_id: task.person_id || null,
        person_name: task.person_name || null,
        company_id: task.company_id || null,
        company_name: task.company_name || null,
        project_id: task.project_id || null,
        project_name: task.project_name || null,
      });

      setSelectedPersonId(task.person_id || null);
      setSelectedPersonName(personLabel);
      setPersonSearch(personLabel);

      setSelectedCompanyId(task.company_id || null);
      setSelectedCompanyName(companyLabel);
      setCompanySearch(companyLabel);

      setSelectedProjectId(task.project_id || null);
      setSelectedProjectName(projectLabel);
      setProjectSearch(projectLabel);
    } catch (err) {
      console.error('Failed to load task:', err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  // Modal open/close lifecycle
  useEffect(() => {
    if (!isOpen) return;
    if (taskId) {
      void loadTask();
    } else {
      resetForm();
    }
  }, [isOpen, taskId, loadTask]);

  // ─── Typeahead searches ───────────────────────────────────────────────────

  const searchPeople = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setPersonSuggestions([]);
      setShowPersonSuggestions(false);
      return;
    }

    setLoadingPeople(true);
    try {
      const res = await fetch(`/api/people?q=${encodeURIComponent(q)}&page_size=${MAX_SUGGESTIONS}`);
      if (!res.ok) return;
      const json = (await res.json()) as { success: boolean; data: PersonSuggestion[] };
      if (!json.success) return;
      setPersonSuggestions(json.data);
      setShowPersonSuggestions(json.data.length > 0);
    } catch {
      // ignore
    } finally {
      setLoadingPeople(false);
    }
  }, []);

  const searchCompanies = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      return;
    }

    setLoadingCompanies(true);
    try {
      const res = await fetch(`/api/companies?q=${encodeURIComponent(q)}&page_size=${MAX_SUGGESTIONS}`);
      if (!res.ok) return;
      const json = (await res.json()) as { success: boolean; data: CompanySuggestion[] };
      if (!json.success) return;
      setCompanySuggestions(json.data);
      setShowCompanySuggestions(json.data.length > 0);
    } catch {
      // ignore
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const searchProjects = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setProjectSuggestions([]);
      setShowProjectSuggestions(false);
      return;
    }

    setLoadingProjects(true);
    try {
      const res = await fetch(`/api/projects?q=${encodeURIComponent(q)}&page_size=${MAX_SUGGESTIONS}`);
      if (!res.ok) return;
      const json = (await res.json()) as { success: boolean; data: ProjectSuggestion[] };
      if (!json.success) return;
      setProjectSuggestions(json.data);
      setShowProjectSuggestions(json.data.length > 0);
    } catch {
      // ignore
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => void searchPeople(personSearch), 300);
    return () => clearTimeout(timer);
  }, [isOpen, personSearch, searchPeople]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => void searchCompanies(companySearch), 300);
    return () => clearTimeout(timer);
  }, [isOpen, companySearch, searchCompanies]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => void searchProjects(projectSearch), 300);
    return () => clearTimeout(timer);
  }, [isOpen, projectSearch, searchProjects]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    const initialPersonLabel = (loadedLinks?.person_name ?? '').trim() || (loadedLinks?.person_id ?? '');
    const initialCompanyLabel = (loadedLinks?.company_name ?? '').trim() || (loadedLinks?.company_id ?? '');
    const initialProjectLabel = (loadedLinks?.project_name ?? '').trim() || (loadedLinks?.project_id ?? '');

    // Validation: if the user has typed something but hasn't selected an option, block save.
    // Exception: if editing an existing task and the field is unchanged from its initial label (legacy name-only cases).
    if (personSearch.trim() && !selectedPersonId && personSearch.trim() !== initialPersonLabel) {
      setError('Please select a person from the suggestions, or clear the field.');
      return;
    }
    if (companySearch.trim() && !selectedCompanyId && companySearch.trim() !== initialCompanyLabel) {
      setError('Please select a company from the suggestions, or clear the field.');
      return;
    }
    if (projectSearch.trim() && !selectedProjectId && projectSearch.trim() !== initialProjectLabel) {
      setError('Please select a project from the suggestions, or clear the field.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload: any = {
        title: title.trim(),
        description: description.trim() || undefined,
        status,
        owner: owner.trim() || undefined,
        due_date: dueDate || undefined,
        task_type: taskType || undefined,
      };

      if (taskId) {
        // PATCH: only send link fields if they have changed.
        const linkUpdates: any = {};

        const initialPersonId = loadedLinks?.person_id ?? null;
        const initialPersonHasValue = Boolean(initialPersonId || (loadedLinks?.person_name ?? '').trim());
        if (selectedPersonId && selectedPersonId !== initialPersonId) {
          linkUpdates.person_id = selectedPersonId;
        } else if (initialPersonHasValue && personSearch.trim() === '' && selectedPersonId === null) {
          linkUpdates.person_id = null;
        }

        const initialCompanyId = loadedLinks?.company_id ?? null;
        const initialCompanyHasValue = Boolean(initialCompanyId || (loadedLinks?.company_name ?? '').trim());
        if (selectedCompanyId && selectedCompanyId !== initialCompanyId) {
          linkUpdates.company_id = selectedCompanyId;
        } else if (initialCompanyHasValue && companySearch.trim() === '' && selectedCompanyId === null) {
          linkUpdates.company_id = null;
        }

        const initialProjectId = loadedLinks?.project_id ?? null;
        const initialProjectHasValue = Boolean(initialProjectId || (loadedLinks?.project_name ?? '').trim());
        if (selectedProjectId && selectedProjectId !== initialProjectId) {
          linkUpdates.project_id = selectedProjectId;
        } else if (initialProjectHasValue && projectSearch.trim() === '' && selectedProjectId === null) {
          linkUpdates.project_id = null;
        }

        Object.assign(payload, linkUpdates);
      } else {
        // POST: only include link fields if selected.
        if (selectedPersonId) payload.person_id = selectedPersonId;
        if (selectedCompanyId) payload.company_id = selectedCompanyId;
        if (selectedProjectId) payload.project_id = selectedProjectId;
      }

      if (taskId) {
        const res = await fetch(`/api/github-ops-tasks/${taskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('update failed');
        const json = (await res.json()) as { success: boolean };
        if (!json.success) throw new Error('api failed');
      } else {
        const res = await fetch('/api/github-ops-tasks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('create failed');
        const json = (await res.json()) as { success: boolean };
        if (!json.success) throw new Error('api failed');
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save task:', err);
      setError('Failed to save task. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={taskId ? 'Edit Task' : 'Create New Task'}>
      {loading ? (
        <div className="task-modal-loading">Loading task...</div>
      ) : (
        <form onSubmit={handleSubmit} className="task-modal-form">
          {error && <div className="task-modal-error">{error}</div>}

          <div className="task-modal-field">
            <label htmlFor="task-title">
              Title <span className="required">*</span>
            </label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              required
            />
          </div>

          <div className="task-modal-field">
            <label htmlFor="task-description">Description</label>
            <textarea
              id="task-description"
              className="task-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter task description"
              rows={4}
            />
          </div>

          <div className="task-modal-row">
            <div className="task-modal-field">
              <label htmlFor="task-status">Status</label>
              <select
                id="task-status"
                className="task-modal-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="To do">To do</option>
                <option value="Doing">Doing</option>
                <option value="Done">Done</option>
              </select>
            </div>

            <div className="task-modal-field">
              <label htmlFor="task-type">Task Type</label>
              <select
                id="task-type"
                className="task-modal-select"
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
              >
                <option value="">Select type...</option>
                <option value="Prospecting">Prospecting</option>
                <option value="Proposal">Proposal</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Invoicing">Invoicing</option>
                <option value="Payment chase">Payment chase</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="task-modal-row">
            <div className="task-modal-field">
              <label htmlFor="task-owner">Owner</label>
              <Input
                id="task-owner"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="Enter owner name"
              />
            </div>

            <div className="task-modal-field">
              <label htmlFor="task-due-date">Due Date</label>
              <Input
                id="task-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          <div className="task-modal-section">
            <h4 className="task-modal-section-title">Link to CRM Records</h4>
            <p className="task-modal-section-desc">Start typing to search CRM records, then select one to link.</p>

            {/* Person */}
            <div className="company-field-wrapper">
              <Input
                id="task-person"
                label="Person"
                value={personSearch}
                onChange={(e) => {
                  const next = e.target.value;
                  setPersonSearch(next);
                  if (selectedPersonName && next !== selectedPersonName) {
                    setSelectedPersonId(null);
                    setSelectedPersonName('');
                  }
                }}
                onBlur={() => setTimeout(() => setShowPersonSuggestions(false), 150)}
                onFocus={() => {
                  if (personSuggestions.length > 0) setShowPersonSuggestions(true);
                }}
                placeholder="Search for a person…"
                autoComplete="off"
              />
              {loadingPeople && (
                <div className="company-suggestions-loading">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {showPersonSuggestions && personSuggestions.length > 0 && (
                <ul className="company-suggestions">
                  {personSuggestions.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="company-suggestion-item"
                        onMouseDown={() => {
                          setSelectedPersonId(p.id);
                          setSelectedPersonName(p.full_name);
                          setPersonSearch(p.full_name);
                          setPersonSuggestions([]);
                          setShowPersonSuggestions(false);
                        }}
                      >
                        {p.full_name}
                        {p.company_name && (
                          <span className="task-typeahead-secondary">— {p.company_name}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedPersonId && (
                <p className="company-field-hint" style={{ color: 'var(--color-success)' }}>
                  ✓ {selectedPersonName} selected
                </p>
              )}
            </div>

            {/* Company */}
            <div className="company-field-wrapper">
              <Input
                id="task-company"
                label="Company"
                value={companySearch}
                onChange={(e) => {
                  const next = e.target.value;
                  setCompanySearch(next);
                  if (selectedCompanyName && next !== selectedCompanyName) {
                    setSelectedCompanyId(null);
                    setSelectedCompanyName('');
                  }
                }}
                onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 150)}
                onFocus={() => {
                  if (companySuggestions.length > 0) setShowCompanySuggestions(true);
                }}
                placeholder="Search for a company…"
                autoComplete="off"
              />
              {loadingCompanies && (
                <div className="company-suggestions-loading">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {showCompanySuggestions && companySuggestions.length > 0 && (
                <ul className="company-suggestions">
                  {companySuggestions.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="company-suggestion-item"
                        onMouseDown={() => {
                          setSelectedCompanyId(c.id);
                          setSelectedCompanyName(c.company_name);
                          setCompanySearch(c.company_name);
                          setCompanySuggestions([]);
                          setShowCompanySuggestions(false);
                        }}
                      >
                        {c.company_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedCompanyId && (
                <p className="company-field-hint" style={{ color: 'var(--color-success)' }}>
                  ✓ {selectedCompanyName} selected
                </p>
              )}
            </div>

            {/* Project */}
            <div className="company-field-wrapper">
              <Input
                id="task-project"
                label="Project"
                value={projectSearch}
                onChange={(e) => {
                  const next = e.target.value;
                  setProjectSearch(next);
                  if (selectedProjectName && next !== selectedProjectName) {
                    setSelectedProjectId(null);
                    setSelectedProjectName('');
                  }
                }}
                onBlur={() => setTimeout(() => setShowProjectSuggestions(false), 150)}
                onFocus={() => {
                  if (projectSuggestions.length > 0) setShowProjectSuggestions(true);
                }}
                placeholder="Search for a project…"
                autoComplete="off"
              />
              {loadingProjects && (
                <div className="company-suggestions-loading">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {showProjectSuggestions && projectSuggestions.length > 0 && (
                <ul className="company-suggestions">
                  {projectSuggestions.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="company-suggestion-item"
                        onMouseDown={() => {
                          setSelectedProjectId(p.id);
                          setSelectedProjectName(p.project_name);
                          setProjectSearch(p.project_name);
                          setProjectSuggestions([]);
                          setShowProjectSuggestions(false);
                        }}
                      >
                        {p.project_name}
                        {p.company_name && (
                          <span className="task-typeahead-secondary">— {p.company_name}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedProjectId && (
                <p className="company-field-hint" style={{ color: 'var(--color-success)' }}>
                  ✓ {selectedProjectName} selected
                </p>
              )}
            </div>
          </div>

          <div className="task-modal-actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : taskId ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default TaskModal;
